import {
  DRAFT_OMIT_KEYS,
  formValuesFromItem,
  itemHasDraft,
  typeUsesPackedText,
} from "./cmsDraft";
import { isConnectSplashType } from "./connectSplashConfig";
import { isJoinInviteType } from "./joinInviteConfig";

export const PREVIEW_STORAGE_KEY = "ms1-admin-preview-overlay";
export const PREVIEW_TTL_MS = 2 * 60 * 60 * 1000; // 2h
export const PREVIEW_ENVELOPE_VERSION = 2;
export const PREVIEW_OVERLAY_EVENT = "ms1-preview-overlay-changed";

export const PREVIEW_QUERY = {
  flag: "preview",
  id: "previewId",
  type: "previewType",
  nonce: "previewNonce",
  splash: "connectsplash",
};

const PREVIEW_TYPE_LABELS = {
  joinInvite: "Join Invite",
  connectSplash: "Connect Splash",
  about: "About",
  noticeOne: "Notice One",
  noticeTwo: "Notice Two",
  noticeThree: "Notice Three",
  catalog: "catalog item",
  song: "catalog item",
  album: "catalog item",
  playlist: "catalog item",
  service: "catalog item",
};

function safeStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function emitPreviewOverlayChanged() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(PREVIEW_OVERLAY_EVENT));
  } catch {
    /* ignore */
  }
}

export function makePreviewNonce() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function isAdminPreviewSession({ user, search } = {}) {
  if (!user || user.isAdmin !== true || !user.token) return false;
  const params = new URLSearchParams(search || "");
  return params.get(PREVIEW_QUERY.flag) === "1";
}

export function parsePreviewQuery(search) {
  const params = new URLSearchParams(search || "");
  return {
    on: params.get(PREVIEW_QUERY.flag) === "1",
    id: params.get(PREVIEW_QUERY.id) || "",
    type: params.get(PREVIEW_QUERY.type) || "",
    nonce: params.get(PREVIEW_QUERY.nonce) || "",
    splash: params.get(PREVIEW_QUERY.splash) || "",
  };
}

function cloneValues(values) {
  if (!values || typeof values !== "object") return {};
  const next = { ...values };
  DRAFT_OMIT_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

function typesMatch(a, b) {
  return (
    String(a || "")
      .trim()
      .toLowerCase() ===
    String(b || "")
      .trim()
      .toLowerCase()
  );
}

function isOneActiveType(type) {
  return isJoinInviteType(type) || isConnectSplashType(type);
}

export function previewItemKey(item) {
  const section = item?.section || "content";
  const identifier =
    item?.identifier ||
    item?.values?.identifier ||
    item?.values?._localId ||
    "";
  return `${section}::${String(identifier)}`;
}

export function previewItemLabel(item) {
  const type = item?.type || item?.values?.type || "";
  const title = item?.values?.title;
  if (
    item?.section === "catalog" ||
    PREVIEW_TYPE_LABELS[type] === "catalog item"
  ) {
    if (title) return String(title);
  }
  return PREVIEW_TYPE_LABELS[type] || type || "item";
}

export function normalizePreviewSet(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  if (Array.isArray(raw.items)) {
    return {
      v: PREVIEW_ENVELOPE_VERSION,
      nonce: raw.nonce || "",
      savedAt: Number(raw.savedAt) || 0,
      items: raw.items.filter((item) => item && typeof item === "object"),
    };
  }
  if (raw.values || raw.identifier || raw.type) {
    return {
      v: PREVIEW_ENVELOPE_VERSION,
      nonce: raw.nonce || "",
      savedAt: Number(raw.savedAt) || 0,
      items: [
        {
          section: raw.section || "content",
          identifier: raw.identifier || raw.values?.identifier || "",
          type: raw.type || raw.values?.type || "",
          values:
            raw.values && typeof raw.values === "object" ? raw.values : {},
          source: raw.source || "unsaved",
        },
      ],
    };
  }
  return null;
}

export function previewSetItems(overlay) {
  return normalizePreviewSet(overlay)?.items || [];
}

function sanitizeItem(item) {
  const values = cloneValues(item?.values);
  const type = item?.type || values.type || "";
  if (typeUsesPackedText(type)) {
    delete values.text;
  }
  return {
    section: item?.section || "content",
    identifier: item?.identifier || values.identifier || values._localId || "",
    type,
    values,
    source: item?.source || "unsaved",
  };
}

export function writePreviewSet(set) {
  const storage = safeStorage();
  if (!storage) return { ok: false, error: "no-storage" };
  try {
    const items = (set?.items || []).map(sanitizeItem);
    const payload = {
      v: PREVIEW_ENVELOPE_VERSION,
      nonce: set?.nonce || makePreviewNonce(),
      savedAt: Date.now(),
      items,
    };
    storage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(payload));
    emitPreviewOverlayChanged();
    return { ok: true, overlay: payload };
  } catch (error) {
    return { ok: false, error };
  }
}

/** Writes a one-item set (replaces any existing pins). Prefer upsertPreviewItem. */
export function writePreviewOverlay(overlay) {
  return writePreviewSet({
    nonce: overlay?.nonce,
    items: [overlay],
  });
}

export function readPreviewOverlay() {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const set = normalizePreviewSet(parsed);
    if (!set) {
      storage.removeItem(PREVIEW_STORAGE_KEY);
      return null;
    }
    const savedAt = Number(set.savedAt) || 0;
    if (!savedAt || Date.now() - savedAt > PREVIEW_TTL_MS) {
      storage.removeItem(PREVIEW_STORAGE_KEY);
      return null;
    }
    return set;
  } catch {
    try {
      storage.removeItem(PREVIEW_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
}

export function clearPreviewOverlay() {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(PREVIEW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  emitPreviewOverlayChanged();
}

export function overlayMatchesQuery(overlay, query) {
  if (!overlay || !query) return false;
  const set = normalizePreviewSet(overlay);
  const nonce = set?.nonce || overlay.nonce;
  if (!query.nonce || String(nonce) !== String(query.nonce)) {
    return false;
  }
  return true;
}

export function isItemInPreviewSet({ section, identifier, values } = {}) {
  const items = previewSetItems(readPreviewOverlay());
  const key = previewItemKey({
    section,
    identifier: identifier || values?.identifier || values?._localId,
    values,
  });
  if (key.endsWith("::")) return false;
  return items.some((item) => previewItemKey(item) === key);
}

export function upsertPreviewItem(item) {
  const incoming = sanitizeItem(item);
  if (!incoming.identifier && !incoming.type) {
    return { ok: false, error: "missing-item" };
  }
  const current = readPreviewOverlay();
  const nonce = current?.nonce || makePreviewNonce();
  let items = current ? [...current.items] : [];
  const key = previewItemKey(incoming);
  items = items.filter((existing) => previewItemKey(existing) !== key);

  let replaced = null;
  if (isOneActiveType(incoming.type)) {
    const previous = items.find((existing) =>
      typesMatch(existing.type, incoming.type),
    );
    if (previous) {
      replaced = previous;
      items = items.filter(
        (existing) => !typesMatch(existing.type, incoming.type),
      );
    }
  }

  items.push(incoming);
  const written = writePreviewSet({ nonce, items });
  return {
    ...written,
    replaced,
    itemCount: written.overlay?.items?.length || items.length,
  };
}

export function removePreviewItem({ section, identifier, values } = {}) {
  const current = readPreviewOverlay();
  if (!current) return { ok: true, overlay: null, itemCount: 0 };
  const key = previewItemKey({ section, identifier, values });
  const items = current.items.filter((item) => previewItemKey(item) !== key);
  if (items.length === 0) {
    clearPreviewOverlay();
    return { ok: true, overlay: null, itemCount: 0 };
  }
  const written = writePreviewSet({ nonce: current.nonce, items });
  return { ...written, itemCount: items.length };
}

export function buildPreviewItemFromForm({
  values,
  section,
  dirty,
  sourceItem,
} = {}) {
  const overlayValues = cloneValues(values);
  if (typeUsesPackedText(values?.type)) {
    delete overlayValues.text;
  }
  return {
    section: section || "content",
    identifier: values?.identifier || values?._localId || "",
    type: values?.type || "",
    values: overlayValues,
    source: dirty
      ? "unsaved"
      : itemHasDraft(sourceItem)
        ? "saved-draft"
        : "unsaved",
  };
}

export function previewTypeForItem(section, type) {
  if (section === "catalog") return "catalog";
  return type || "";
}

export function buildPreviewUrl({
  origin,
  type,
  identifier,
  nonce,
  splash,
} = {}) {
  const base =
    origin ||
    (typeof window !== "undefined" && window.location
      ? window.location.origin
      : "");
  const params = new URLSearchParams();
  params.set(PREVIEW_QUERY.flag, "1");
  if (type) params.set(PREVIEW_QUERY.type, String(type));
  if (identifier) params.set(PREVIEW_QUERY.id, String(identifier));
  if (nonce) params.set(PREVIEW_QUERY.nonce, String(nonce));

  let hash = "";
  const t = String(type || "");
  if (t === "joinInvite") {
    params.set("joininvite", "1");
  } else if (t === "connectSplash") {
    params.set(
      PREVIEW_QUERY.splash,
      splash === "confirmed" ? "confirmed" : "pending",
    );
  } else if (t === "about") {
    hash = "#about";
  } else if (t === "noticeOne") {
    hash = "#noticeOne";
  } else if (t === "noticeTwo") {
    hash = "#noticeTwo";
  } else if (t === "noticeThree") {
    hash = "#noticeThree";
  } else if (t === "catalog") {
    hash = "#music";
  }

  return `${base}/?${params.toString()}${hash}`;
}

export function stripPreviewSearch(search) {
  const params = new URLSearchParams(search || "");
  [
    PREVIEW_QUERY.flag,
    PREVIEW_QUERY.id,
    PREVIEW_QUERY.type,
    PREVIEW_QUERY.nonce,
    PREVIEW_QUERY.splash,
    "joininvite",
    "connected",
    "confirmed",
  ].forEach((key) => params.delete(key));
  const next = params.toString();
  return next ? `?${next}` : "";
}

export function findStoreKey(store, overlay) {
  const items = store && typeof store === "object" ? store : {};
  const identifier = overlay?.identifier || overlay?.values?.identifier;
  const storeKey = overlay?.values?._storeKey;
  const localId = overlay?.values?._localId;

  if (identifier != null && String(identifier) !== "") {
    const id = String(identifier);
    const hit = Object.keys(items).find(
      (key) => items[key] && String(items[key].identifier) === id,
    );
    if (hit) return hit;
  }
  if (storeKey != null && String(storeKey) !== "") {
    const sk = String(storeKey);
    const hit = Object.keys(items).find(
      (key) => items[key] && String(items[key]._storeKey) === sk,
    );
    if (hit) return hit;
  }
  if (localId != null && String(localId) !== "") {
    const lid = String(localId);
    if (Object.prototype.hasOwnProperty.call(items, lid)) return lid;
    const hit = Object.keys(items).find(
      (key) => items[key] && String(items[key]._localId) === lid,
    );
    if (hit) return hit;
  }
  return String(identifier || overlay?.nonce || "preview-item");
}

export function applyOverlayValues(baseItem, overlay) {
  const base = baseItem && typeof baseItem === "object" ? { ...baseItem } : {};
  const values =
    overlay?.values && typeof overlay.values === "object"
      ? { ...overlay.values }
      : {};
  const itemType = overlay?.type || values.type || base.type;
  if (typeUsesPackedText(itemType)) {
    delete values.text;
  }
  DRAFT_OMIT_KEYS.forEach((key) => {
    delete values[key];
  });
  const next = {
    ...base,
    ...values,
    identifier: values.identifier || overlay?.identifier || base.identifier,
    type: itemType && itemType !== "catalog" ? itemType : values.type || base.type,
    active: "true",
  };
  if (typeUsesPackedText(itemType)) {
    delete next.text;
  }
  if (base._id != null) next._id = base._id;
  if (base._storeKey != null) next._storeKey = base._storeKey;
  return next;
}

function findItemByIdentifier(store, identifier) {
  if (!store || identifier == null || String(identifier) === "") return null;
  const id = String(identifier);
  return (
    Object.values(store).find(
      (item) => item && String(item.identifier) === id,
    ) || null
  );
}

function resolveSavedDraftPayload({ content, catalog, query }) {
  const id = query?.id;
  if (!id) return null;
  const preferCatalog = query?.type === "catalog";
  const primary = preferCatalog ? catalog : content;
  const secondary = preferCatalog ? content : catalog;
  const item =
    findItemByIdentifier(primary, id) || findItemByIdentifier(secondary, id);
  if (!item || !itemHasDraft(item)) return null;
  const inCatalog = findItemByIdentifier(catalog, id) === item;
  return {
    section: inCatalog ? "catalog" : "content",
    identifier: id,
    type: item.type,
    values: formValuesFromItem(item),
    source: "saved-draft",
  };
}

function applyOneItem(content, catalog, payload) {
  const isCatalog = payload.section === "catalog";
  const sourceStore = isCatalog ? catalog : content;
  const nextStore = {
    ...(sourceStore && typeof sourceStore === "object" ? sourceStore : {}),
  };
  const key = findStoreKey(nextStore, payload);
  const base =
    nextStore[key] && typeof nextStore[key] === "object" ? nextStore[key] : {};
  nextStore[key] = applyOverlayValues(base, payload);

  if (isOneActiveType(payload.type)) {
    Object.keys(nextStore).forEach((k) => {
      if (k === key) return;
      const storeItem = nextStore[k];
      if (storeItem && typesMatch(storeItem.type, payload.type)) {
        nextStore[k] = { ...storeItem, active: "false" };
      }
    });
  }

  if (isCatalog) {
    return { content, catalog: nextStore };
  }
  return { content: nextStore, catalog };
}

export function mergePreviewIntoStores({
  content,
  catalog,
  overlay,
  query,
  user,
  search,
} = {}) {
  const parsedQuery = query || parsePreviewQuery(search);
  if (!isAdminPreviewSession({ user, search })) {
    return { content, catalog };
  }

  let items = [];
  const set = normalizePreviewSet(overlay);
  if (set && overlayMatchesQuery(set, parsedQuery)) {
    items = set.items;
  } else {
    const fallback = resolveSavedDraftPayload({
      content,
      catalog,
      query: parsedQuery,
    });
    if (fallback) items = [fallback];
  }
  if (!items.length) {
    return { content, catalog };
  }

  return items.reduce(
    (stores, payload) => applyOneItem(stores.content, stores.catalog, payload),
    { content, catalog },
  );
}

function formatLabelList(labels) {
  if (labels.length <= 1) return labels[0] || "item";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

export function previewBannerCopy(overlay, query) {
  const items = previewSetItems(overlay);
  const labels = items.map(previewItemLabel).filter(Boolean);
  if (!labels.length) {
    const type = overlay?.type || query?.type || "item";
    labels.push(PREVIEW_TYPE_LABELS[type] || type);
  }
  return `Previewing unpublished ${formatLabelList(labels)} — visitors see the live version.`;
}

export function openAdminPreview({
  values,
  section,
  dirty,
  sourceItem,
  splash,
  origin,
} = {}) {
  const item = buildPreviewItemFromForm({
    values,
    section,
    dirty,
    sourceItem,
  });
  const written = upsertPreviewItem(item);
  const nonce = written.overlay?.nonce || makePreviewNonce();
  const previewType = previewTypeForItem(section, values?.type);
  const url = buildPreviewUrl({
    origin,
    type: previewType,
    identifier: item.identifier,
    nonce,
    splash,
  });
  if (typeof window !== "undefined" && typeof window.open === "function") {
    window.open(url, "_blank", "noopener,noreferrer");
  }
  return {
    ok: written.ok,
    url,
    error: written.error,
    nonce,
    replaced: written.replaced || null,
    itemCount: written.itemCount || 0,
  };
}
